// The terminal row primitive: one row = one line of finished ANSI.
//
// This is the atom two hosts already share without knowing it. In tsh, every
// pane's render boundary is `Vec<String>` — one fully painted ANSI line per
// terminal row (`Pane::render` in tento-tui-pane, `PaneSlot.lines` in
// tento-tui-core's `Frame`). In the browser, the session replay decodes the
// same bytes into spans and paints them. Everything richer — tento-shapes'
// `term.block/v1` rows of styled spans, wrap, continuation prefixes — flattens
// INTO this form before a terminal ever draws it, which is exactly why a web
// painter of the same form is faithful by construction: it is painting the
// bytes the terminal painted, not an imitation of them.
//
// What a row deliberately is NOT:
//
//   - It has no `mode`. Typed/printed/blank is REPLAY vocabulary — a claim
//     about how a line arrives over time — and a frame from a live pane has
//     no such thing. The replay layers its classification on top.
//   - It is not a virtual description. `paintInto` writes a real element,
//     because the replay engine mutates rows mid-animation and appends the
//     caret into them; a row that re-renders from data on every beat would
//     break that contract.
//
// The decoding itself lives in `../session-replay-ansi` — one decoder, fully
// unit-tested, kept where its tests already are. This module is the ROW layer
// over it: the whole-row ground, the painter, and the default element.

import {
  blankSgr,
  decodeAnsi,
  type Sgr,
  type Span,
} from "./ansi";

export namespace TermRow {
  /** One terminal row, decoded. */
  export interface Row {
    /** The row with every escape removed — what a width check measures. */
    text: string;
    /** The same text, cut into runs by the SGR state in force. */
    spans: Span[];
    /**
     * The ground for the WHOLE row, when every painted run shares one — a
     * terminal band. Null when the row has no ground or changes ground
     * partway.
     *
     * Row-level rather than span-level because of how CSS paints: an inline
     * box only fills its font box, so with any leading above 1 a span
     * background draws a stripe and consecutive banded rows read as stripes
     * with gaps. A block-level ground fills the line box and adjacent banded
     * rows meet with no seam — which is what the terminal does, because there
     * the band is a property of the cell and cells tile exactly.
     */
    bg: Option<string>;
  }

  /** Decode one line of finished ANSI into a row. Pure. */
  export function parse(raw: string): Row {
    const { text, spans } = decodeAnsi(raw);
    return { text, spans, bg: rowBackground(spans) };
  }

  /** The ground a run actually paints: reverse video swaps ink and ground,
   * so the painted band is the FG (or the inherited ink, as currentColor) —
   * reading raw `sgr.bg` under reverse names the colour the run is NOT
   * painting. Both painting paths below resolve through this, so the
   * row-band test and the per-span band can never disagree again. */
  export function effectiveGround(sgr: Sgr): Option<string> {
    return sgr.reverse ? sgr.fg ?? "currentColor" : sgr.bg;
  }

  /** The one ground shared by every painted run on a line, or null. */
  export function rowBackground(spans: Span[]): Option<string> {
    const painted = spans.filter((span) => span.text.length > 0);
    if (painted.length === 0) return null;
    const first = effectiveGround(painted[0].sgr);
    if (first === null) return null;
    return painted.every((span) => effectiveGround(span.sgr) === first)
      ? first
      : null;
  }

  /** True when a state needs no span of its own over the row's ground. */
  function plain(sgr: Sgr, rowBg: Option<string>): boolean {
    return (
      !sgr.fg &&
      !sgr.bold &&
      !sgr.dim &&
      !sgr.italic &&
      !sgr.underline &&
      !sgr.strike &&
      !sgr.reverse &&
      (!sgr.bg || sgr.bg === rowBg)
    );
  }

  /**
   * Write a row's spans into an element, colour and all.
   *
   * The element is written, not returned, because callers own their row
   * elements — the replay's engine appends carets into them and retypes their
   * text; a chat block recycles them. The overwhelmingly common case is one
   * unstyled run, which paints as a bare text node: a span per line across a
   * 600-line transcript is a lot of DOM for nothing.
   */
  export function paintInto($el: HTMLElement, row: Row): void {
    // Always assigned: row elements are recycled, and a stale ground or band
    // class would survive into a row that has none. The class is what lets a
    // stylesheet put air between banded blocks without knowing any colour.
    const rowBg = row.bg;
    $el.style.background = rowBg ?? "";
    $el.classList.toggle("banded", rowBg !== null);

    if (
      row.spans.length <= 1 &&
      (row.spans.length === 0 || plain(row.spans[0].sgr, rowBg))
    ) {
      $el.textContent = row.text;
      return;
    }
    $el.replaceChildren();
    for (const span of row.spans) {
      if (span.text.length === 0) continue;
      if (plain(span.sgr, rowBg)) {
        $el.appendChild(document.createTextNode(span.text));
        continue;
      }
      $el.appendChild($span(span, rowBg));
    }
  }

  /** One styled run. Exported for callers composing rows themselves. */
  export function $span(span: Span, rowBg: Option<string>): HTMLElement {
    const { fg, bg, bold, dim, italic, underline, strike, reverse } = span.sgr;
    const $s = document.createElement("span");
    $s.textContent = span.text;

    // Reverse swaps ink and ground, and the swap needs the defaults: the ink
    // default is whatever colour this row inherits (currentColor expresses
    // that exactly), and the ground default is the panel's — which no
    // primitive can know, so it is a custom property with the terminal's
    // conventional dark ground as the fallback. A caller whose panel is not
    // near-black sets --session-replay-ansi-ground once and every reverse run follows.
    const ink = reverse
      ? bg ?? "var(--session-replay-ansi-ground, #000000)"
      : fg;
    const ground = effectiveGround(span.sgr);

    if (ink) $s.style.color = ink;
    // A background run is a painted BAND whose width is carried by the spaces
    // inside it — which is why a terminal screen sets white-space: pre.
    // Nothing here pads or trims.
    if (ground && (reverse || ground !== rowBg)) $s.style.background = ground;
    if (bold) $s.style.fontWeight = "700";
    // Dim is opacity rather than a darker colour: it has to work over any
    // ground, and the design owns the ground.
    if (dim) $s.style.opacity = "var(--session-replay-ansi-dim, 0.55)";
    if (italic) $s.style.fontStyle = "italic";
    // Underline and strike are one property; both at once is legal SGR.
    if (underline || strike) {
      $s.style.textDecoration = [
        underline ? "underline" : "",
        strike ? "line-through" : "",
      ]
        .filter((part) => part.length > 0)
        .join(" ");
    }
    return $s;
  }

  /** The default row element: a block line, painted. */
  export function $row(row: Row): HTMLElement {
    const $el = document.createElement("div");
    $el.className = "line";
    paintInto($el, row);
    return $el;
  }
}

// Re-exported so a term-level caller has one import for the whole layer.
export { blankSgr, decodeAnsi };
export type { Sgr, Span };
